'use client';
import { getCTA } from '@/utils/getCTA';
import { getColorScheme } from '@/utils/getColorScheme'; // Ensure this path is correct
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

function isRemoteUrl(url) {
    return url.startsWith('http');
}

function makeLocalUrlRefImages(url) {
    if (url.startsWith('/images')) {
        return url;
    }
    return '/images/' + url.split('/').pop();
}

export default function CredentialsLine(props) {
    const {
        Header,
        Body,
        Badges = [],
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        ColorScheme = 'secondary',
        VerticalSpacing = 'medium',
    } = props;

    // Retrieve color scheme settings
    const {
        backgroundColor,
        textColor,
        headerColor,
        badgeColor,
        secondaryButton,
        thirdButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    // Define vertical spacing
    const vSpace = getVerticalSpacing(VerticalSpacing);

    const numBadges = Math.min(Badges.length, 20);
    const [hoveredBadge, setHoveredBadge] = useState(null);
    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: 'justify-center',
            wrapSpacing: 'mt-10 lg:mt-16',
            className: 'w-full',
            buttonsClassName:
                'flex flex-wrap items-center justify-center gap-4',
        }
    );

    return (
        <section
            className={`editable-component relative ${backgroundColor} ${vSpace} px-6 lg:px-8`}
            style={backgroundStyle}
            data-json="credentialsline"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto mb-6 w-full text-center md:mb-10 lg:mx-0">
                    <MarkDown
                        variant="h2"
                        className={`mx-auto max-w-2xl font-display text-3xl tracking-tight lg:max-w-4xl ${headerColor} lg:text-4xl`}
                    >
                        {Header}
                    </MarkDown>
                    <MarkDown
                        className={`mx-auto mt-6 max-w-2xl text-lg font-text leading-8 lg:max-w-4xl  ${textColor}`}
                    >
                        {Body}
                    </MarkDown>
                </div>
                <div
                    className={`mx-auto flex flex-wrap items-center justify-center gap-8 lg:gap-x-24 ${badgeColor}`}
                >
                    {Badges.slice(0, numBadges).map((Badge, index) =>
                        Badge.Link ? (
                            <a
                                href={Badge.Link}
                                key={Badge.AltText}
                                onMouseEnter={() => setHoveredBadge(index)}
                                onMouseLeave={() => setHoveredBadge(null)}
                                className="inline-flex"
                            >
                                <img
                                    className="max-h-28 max-w-48 object-contain lg:object-center"
                                    src={
                                        isRemoteUrl(Badge.Image)
                                            ? Badge.Image
                                            : makeLocalUrlRefImages(Badge.Image)
                                    }
                                    alt={Badge.AltText}
                                    width={192}
                                    height={112}
                                    style={{
                                        maxWidth: '192px',
                                        maxHeight: '112px',
                                        filter:
                                            hoveredBadge === index
                                                ? 'brightness(0.7)'
                                                : 'none',
                                        transition: 'filter 0.3s ease-in-out',
                                    }}
                                    loading="lazy"
                                />
                            </a>
                        ) : (
                            <img
                                key={Badge.AltText}
                                className="max-h-28 max-w-48 object-contain lg:object-center"
                                src={
                                    isRemoteUrl(Badge.Image)
                                        ? Badge.Image
                                        : makeLocalUrlRefImages(Badge.Image)
                                }
                                alt={Badge.AltText}
                                width={192}
                                height={112}
                                style={{
                                    maxWidth: '192px',
                                    maxHeight: '112px',
                                }}
                                loading="lazy"
                            />
                        )
                    )}
                </div>
                {hasCTAs && CTAs}
            </div>
        </section>
    );
}
